import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, MobileStepper as Stepper } from "@mui/material";
import Shell from "components/shell";
import { useEnrollmentSteps } from "hooks/useEnrollmentSteps";
import { useEnrollmentForm } from "hooks/useEnrollmentForm";
import {
    ENROLLMENT_ERROR_PAGE,
    ENROLLMENT_STEPS_NUMBER,
} from "constants/enrollment";
import { isOver18 } from "utilities";

import {
    accountCreated,
    addShippingAddress,
    checkEligibility,
    createAccount,
    getSponsorRules,
    migrateUser,
    programDetermination,
    verifyShippingAddress,
} from "data";

import { formSections } from "./formSections";
import StepRenderer from "./StepRenderer";
import {
    buildEligibilityPayload,
    buildProgramPayload,
    buildAccountPayload,
    buildShippingAddress,
} from "./apiPayloadUtils";

/**
 * @description This component manages the entire step enrollment flow, including form state, step navigation, and API interactions. It uses custom hooks for managing form data and step logic.
 * @returns {JSX.Element} The rendered step enrollment page component.
 */
export const StepEnrollmentPage = () => {
    const params = useParams();
    const SPONSOR_ID = params.sponsor.toUpperCase();
    const { formData, handleFormChange, resetForm, onSubmit, isSubmitting } =
        useEnrollmentForm(formSections); // Initialize form with defined sections and fields | form state management hook

    const {
        activeStep,
        setActiveStep,
        errorScreen,
        setErrorScreen,
        resetToInitialStep,
    } = useEnrollmentSteps(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_CHECK); // Step management hook

    useEffect(() => {
        fetchSponsorRules();
    }, []);

    const handleFormReset = () => {
        resetForm();
        // Get the sponsor rules again on form reset to populate the dropdowns with correct options.
        fetchSponsorRules();
    };

    /**
     * Fetch sponsor-specific rules and options.
     */
    const fetchSponsorRules = async () => {
        let rules = {};
        try {
            const response = await getSponsorRules(SPONSOR_ID);
            if (!response.ok) {
                throw new Error("Failed to fetch sponsor rules");
            }
            rules = await response.json();
        } catch (error) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.API_ERROR);
            return;
        }


        const treatQuestionOption = rules.treatQuestionOption.map((item) => ({
            label: item.title,
            value: item.key,
        }));
        const preventQuestionOption = rules.preventQuestionOption.map((item) => ({
            label: item.title,
            value: item.key,
        }));

        handleFormChange(
            "internalFormData",
            "discountCode",
            rules.discountCode || null,
        );

        handleFormChange(
            "internalFormData",
            "treatQuestionOption",
            treatQuestionOption,
        );
        handleFormChange(
            "internalFormData",
            "preventQuestionOption",
            preventQuestionOption,
        );
    };

    /**
     * Handles the eligibility check.
     */
    const handleCheckEligibility = async () => {
        if (!isOver18(formData.eligibility.dateOfBirth)) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.UNDER_AGE);
            return;
        }
        const payload = buildEligibilityPayload(SPONSOR_ID, formData);
        try {
            const response = await onSubmit(() =>
                checkEligibility(SPONSOR_ID, payload),
            );
            const result = await response.json();
            handleFormChange(
                "internalFormData",
                "eligibleId",
                result?.eligibleId || null,
            );
            if (result?.eligible) {
                setActiveStep(ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_SUCCESS);
            } else if (!result?.eligible) {
                setErrorScreen(ENROLLMENT_ERROR_PAGE.DEFAULT);
            }
        } catch (error) {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.DEFAULT);
        }
    };

    const handleEligibilitySuccessNext = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_TREAT);

    const handleTreatNext = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_PREVENT);

    /**
     * Handles the care pathway interpretation submission, which determines the user's program based on their responses and eligibility.
     */
    const handleInterpretationSubmit = async () => {
        const payload = buildProgramPayload(formData);
        try {
            const response = await onSubmit(() =>
                programDetermination(SPONSOR_ID, payload),
            );
            const result = await response.json();
            handleFormChange("internalFormData", "programId", result?.programId);
            setActiveStep(ENROLLMENT_STEPS_NUMBER.PROGRAM_RESULT);
        } catch {
            setErrorScreen(ENROLLMENT_ERROR_PAGE.API_ERROR);
        }
    };

    const handleProgramContinue = () =>
        setActiveStep(ENROLLMENT_STEPS_NUMBER.CREATE_ACCOUNT);

    /**
     * Handles account creation for the user. 
     * It also pre-populates the shipping address fields.
     */
    const handleCreateAccount = async () => {
        const payload = buildAccountPayload(SPONSOR_ID, formData);
        try {
            const response = await onSubmit(() => createAccount(payload));
            const data = await response.json();
            if (data.errors) {
                return Promise.reject(data.errors);
            }
            //If account is created  call account created API.
            accountCreated(data?.authToken, data.userId);

            //Store auth token and user id in form state for future API calls
            handleFormChange(
                "internalFormData",
                "authToken",
                data?.authToken || null,
            );
            handleFormChange("internalFormData", "userId", data?.userId || null);

            //prepopulate shipping address with the data from account creation response.
            prefillShippingAddress(data);

            setActiveStep(ENROLLMENT_STEPS_NUMBER.SHIPPING_ADDRESS);
        } catch (errorData) {
            return Promise.reject(errorData);
        }
    };

    const handleMigrateUser = async (loginData) => {
        handleFormChange("internalFormData", "authToken", loginData?.auth_token);
        handleFormChange("internalFormData", "userId", loginData?.public_id);
        handleFormChange("createAccount", "email", loginData?.email);
        handleFormChange("createAccount", "phone", loginData?.phone || formData.createAccount.phone);
        handleFormChange("createAccount", "terms", true);
        handleFormChange("createAccount", "consent", false); //TODO: confirm default value for marketing consent for migrated users
        const payload = buildAccountPayload(SPONSOR_ID, formData);

        try {

            const response = await onSubmit(() => migrateUser(payload, loginData?.public_id, loginData?.auth_token));
            const data = await response.json();
            if (data.errors || !response.ok) {
                return Promise.reject(data.errors);
            }
            setActiveStep(ENROLLMENT_STEPS_NUMBER.SHIPPING_ADDRESS);
        }
        catch (error) {
            return Promise.reject(error);
        }

        const shippingData = {
            firstName: loginData?.first_name,
            lastName: loginData?.last_name,
            address1: loginData?.address1,
            address2: loginData?.address2,
            city: loginData?.city,
            state: loginData?.state,
            postalCode: loginData?.postal_code,
        };
        prefillShippingAddress(shippingData);
    };

    const prefillShippingAddress = (data) => {
        handleFormChange("shipping", "firstName", data?.firstName || null);
        handleFormChange("shipping", "lastName", data?.lastName || null);
        //handleFormChange("shipping", "address1", data?.address1 || null);
        //handleFormChange("shipping", "address2", data?.address2 || null);
        //handleFormChange("shipping", "city", data?.city || null);
        // handleFormChange("shipping", "state", data?.state || null);
        //handleFormChange("shipping", "postalCode", data?.postalCode || null);
    }

    /**
     * Handles the verification of the shipping address before submission. 
     * Provides the address suggestions for users to select from the entered address or the suggested addresses from the API.
     */
    const handleVerifyShippingAddress = async () => {
        const currentAddress = buildShippingAddress(formData);
        return onSubmit(() =>
            verifyShippingAddress(
                currentAddress,
                formData.internalFormData.authToken,
            ),
        );
    };

    /**
     * Handles the submission of the shipping address.
     * @param {*} address The shipping address to be added.
     */
    const handleAddShippingAddress = async (address) => {
        return onSubmit(() =>
            addShippingAddress(address, formData.internalFormData.authToken),
        ).then(() => {
            setActiveStep(ENROLLMENT_STEPS_NUMBER.SUCCESS);
        });
    };

    const getStepProgress = () => {
        if (errorScreen) {
            return 0;
        }
        // If user is in program result step but doesn't have a program, show progress as if they are at the end of the steps since they can only end the flow
        else if (
            activeStep == ENROLLMENT_STEPS_NUMBER.PROGRAM_RESULT &&
            !formData.internalFormData.programId
        ) {
            return Object.keys(ENROLLMENT_STEPS_NUMBER).length - 1;
        }
        return activeStep;
    };

    return (
        <Shell
            confirmation={activeStep === ENROLLMENT_STEPS_NUMBER.SUCCESS}
            Stepper={
                <Box sx={{ width: "100%", mb: 2 }}>
                    <Stepper
                        variant="progress"
                        steps={Object.keys(ENROLLMENT_STEPS_NUMBER).length}
                        position="static"
                        activeStep={getStepProgress()}
                        sx={{
                            width: "100%",
                            flexGrow: 1,
                            bgcolor: "transparent",
                            boxShadow: "none",
                            p: 0,
                            "& .MuiMobileStepper-progress": {
                                width: "100%",
                            },
                            "& .MuiLinearProgress-bar": {
                                bgcolor: errorScreen ? "error.main" : "primary.main",
                            },
                        }}
                    />
                </Box>
            }
        >
            <StepRenderer
                step={activeStep}
                formData={formData}
                errorScreen={errorScreen}
                resetToInitialStep={() => resetToInitialStep(handleFormReset)}
                isSubmitting={isSubmitting}
                handleFormChange={handleFormChange}
                // Pass all the handlers to StepRenderer
                handleCheckEligibility={handleCheckEligibility}
                handleEligibilitySuccessNext={handleEligibilitySuccessNext}
                handleTreatNext={handleTreatNext}
                handleInterpretationSubmit={handleInterpretationSubmit}
                handleProgramContinue={handleProgramContinue}
                handleMigrateUser={handleMigrateUser}
                handleCreateAccount={handleCreateAccount}
                handleAddShippingAddress={handleAddShippingAddress}
                handleVerifyShippingAddress={handleVerifyShippingAddress}
            />
        </Shell>
    );
};
